import React, { useEffect, useMemo } from 'react';

import { useUpdateVehicleOptions, useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { isNewOptionValid } from '~/domain/configurationRule';
import { hasMultipleOptions, isPartOfEim } from '~/domain/features';
import { IConfigurationOptionGroup } from '~/domain/IConfigurationOptionGroup';
import { IConfigurationRule } from '~/domain/IConfigurationRule';
import { SITE_CLIENT } from '~/env';
import { useProductConfigurationQuery } from '~/hooks/useProductConfigurationQuery';

import { createEim } from './createEim';
import { VehicleConfigurations } from './VehicleConfigurations';
import { VehicleConfigurationsTrims } from './VehicleConfigurationsTrims';

const emptyArray: unknown = [];

export const VehicleConfigurationsContainer = React.memo(function VehicleConfigurationsContainer() {
  const {
    data: {
      packSets = emptyArray as IConfigurationOptionGroup[],
      eimPackSets = emptyArray as IConfigurationOptionGroup[],
      rules = emptyArray as IConfigurationRule[],
      client,
      vehicle,
      trims,
      year,
    } = {},
  } = useProductConfigurationQuery();
  const vehicleOptions = useVehicleOptions();
  const updateVehicleOptions = useUpdateVehicleOptions();
  const { eim, features } = vehicleOptions;

  const [validatedEimPackSets, validatedSinglePackSets, validatedMultiPackSets, validatedPackSets] = useMemo(() => {

    const validatedPackSets = packSets.map((packSet: IConfigurationOptionGroup, index: number) => {
      const packs = packSet.packs.map(pack => ({
        ...pack,
        isValid: isNewOptionValid(features, index, pack.code, rules),
      }));
      return {
        ...packSet,
        packs,
      };
    });
    const validatedSinglePackSets = validatedPackSets.filter(x => !isPartOfEim(x) && !hasMultipleOptions(x));
    const validatedMultiPackSets = validatedPackSets.filter(x => !isPartOfEim(x) && hasMultipleOptions(x));
    const validatedEimPackSets = validatedPackSets.filter(isPartOfEim);

    return [validatedEimPackSets, validatedSinglePackSets, validatedMultiPackSets, validatedPackSets];
  }, [features, packSets, rules]);

  useEffect(() => {
    const eimPacks = validatedEimPackSets.flatMap(x => x.packs);
    const selectedEimConfigurationOptions = eimPacks.filter(x => features.includes(x.code));
    const selectedEimCodes = selectedEimConfigurationOptions.map(x => x.code);
    const eim = createEim(selectedEimCodes);

    updateVehicleOptions((prev) => {
      if (eim && prev.eim !== eim) {
        return { ...prev, eim }
      }
      return prev;
    });
  }, [validatedPackSets, features, updateVehicleOptions, validatedEimPackSets]);

  return (
    <>

      {(SITE_CLIENT!=='Subaru') && 
        <>
          <VehicleConfigurations
            eim={eim}
            eimPackSets={eimPackSets}
            packSets={packSets}
            rules={rules}
            validatedPackSets={validatedPackSets}
            validatedEimPackSets={validatedEimPackSets}
            validatedMultiPackSets={validatedMultiPackSets}
            validatedSinglePackSets={validatedSinglePackSets}
          />
        </>
      }

      {(SITE_CLIENT==='Subaru') && 
        <>
          <VehicleConfigurationsTrims
            client={client}
            year={year}
            trims={trims}
            vehicle={vehicle}
          />
        </>
      }

    </>
  )
});
